import React, { useState, useRef, useEffect, useContext } from "react";
import "./AgendaControls.css";
import {
  LuPlus,
  LuChevronDown,
  LuCalendarDays,
  LuLayoutGrid,
  LuCheck,
  LuBookmark,
  LuCheckCircle2,
} from "react-icons/lu";
import {
  AGENDA_CATEGORY_LIST,
  EXPLORE_CATEGORY_LIST,
} from "../../constants/agendaConstants";
import { Language } from "../../App";

export default function AgendaControls({
  viewMode,
  setViewMode,
  selectedCategory,
  setSelectedCategory,
  onCreateClick,
}) {
  const { lang } = useContext(Language) || {};
  const [filterOpen, setFilterOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (viewMode === "saved" && selectedCategory === "mentorship") {
      setSelectedCategory("all");
    }
  }, [viewMode, selectedCategory, setSelectedCategory]);

  const categoriesList =
    viewMode === "saved"
      ? AGENDA_CATEGORY_LIST.filter((cat) => cat.id !== "mentorship")
      : viewMode === "schedules" || viewMode === "completed"
      ? AGENDA_CATEGORY_LIST
      : EXPLORE_CATEGORY_LIST;

  const getFilterLabel = (cat) => {
    if (!cat) return lang?.all_types || "All Types";
    const id = typeof cat === "string" ? cat : cat.id;
    switch (id) {
      case "all":
        return lang?.all_types || "All Types";
      case "event":
        return lang?.events || "Events";
      case "webinar":
        return lang?.webinars || "Webinars";
      case "clinical_session":
        return lang?.clinical_sessions || "Clinical Sessions";
      case "mentorship":
        return lang?.mentorships || "Mentorships";
      default:
        return cat.filterLabel || cat.label || id;
    }
  };

  const currentCategoryObj = categoriesList.find((c) => c.id === selectedCategory);
  const currentLabel = getFilterLabel(currentCategoryObj || selectedCategory);

  const handleTabChange = (mode) => {
    setViewMode(mode);
  };

  return (
    <div className="agenda-controls-bar">
      {/* Left controls: Agenda view switcher */}
      <div className="agenda-controls-left">
        <div className="agenda-view-switcher">
          <button
            className={`agenda-view-tab-btn ${viewMode === "explore" ? "active" : ""}`}
            onClick={() => handleTabChange("explore")}
          >
            <LuLayoutGrid size={15} />
            <span>{lang?.explore || "Explore"}</span>
          </button>
          <button
            className={`agenda-view-tab-btn ${viewMode === "schedules" ? "active" : ""}`}
            onClick={() => handleTabChange("schedules")}
          >
            <LuCalendarDays size={15} />
            <span>{lang?.schedules || "Schedules"}</span>
          </button>
          <button
            className={`agenda-view-tab-btn ${viewMode === "saved" ? "active" : ""}`}
            onClick={() => handleTabChange("saved")}
          >
            <LuBookmark size={15} />
            <span>{lang?.saved || "Saved"}</span>
          </button>
          <button
            className={`agenda-view-tab-btn ${viewMode === "completed" ? "active" : ""}`}
            onClick={() => handleTabChange("completed")}
          >
            <LuCheckCircle2 size={15} />
            <span>{lang?.completed || "Completed"}</span>
          </button>
        </div>
      </div>

      {/* Right controls: Types Dropdown Filter + Create Event Button */}
      <div className="agenda-controls-right">
        {/* Types Filter Dropdown */}
        <div className="agenda-filter-dropdown-wrapper" ref={filterRef}>
          <button
            className="agenda-filter-btn"
            onClick={() => setFilterOpen((prev) => !prev)}
            aria-expanded={filterOpen}
          >
            <span>{currentLabel}</span>
            <LuChevronDown size={15} />
          </button>

          {filterOpen && (
            <div className="agenda-filter-menu">
              {categoriesList.map((cat) => (
                <div
                  key={cat.id}
                  className={`agenda-filter-item ${selectedCategory === cat.id ? "selected" : ""}`}
                  onClick={() => {
                    setSelectedCategory(cat.id);
                    setFilterOpen(false);
                  }}
                >
                  <span>{getFilterLabel(cat)}</span>
                  {selectedCategory === cat.id && <LuCheck size={14} color="#00BADA" />}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Create Event Button */}
        <button className="agenda-create-btn" onClick={onCreateClick}>
          <LuPlus size={16} />
          <span>{lang?.create_agenda}</span>
        </button>
      </div>
    </div>
  );
}